/* eslint-disable indent */

import React, { MouseEventHandler, SVGProps, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import NodeInfo from "./NodeInfo";
import "./SceneNode.scss";

export type Props = SVGProps<SVGSVGElement> & {
  title: string;
  valid?: boolean;
  onClick?: MouseEventHandler<SVGSVGElement>;
  sceneType?: string;
  content?: any;
  ctName?: string;
  details?: any;
  unlinked?: boolean;
  startScene?: boolean;
  width?: number | string;
  height?: number | string;
  viewBox?: string;
  onCopyScene?: (info: { contentSceneId: number; contentId: number }) => void;
};

type Sized = {
  HEIGHT: number;
  WIDTH: number;
};

const SVGNodeScene: React.FC<Props> & Sized = ({
  title,
  valid,
  onClick,
  content,
  sceneType,
  ctName,
  details,
  startScene,
  width,
  height,
  viewBox,
  onCopyScene,
  ...svgProps
}: any) => {
  const x = svgProps.x;
  const { t } = useTranslation();

  const tspans = useMemo(() => buildTSpans(title), [title]);
  const strokeWidth = 2;

  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return undefined;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  const handlePlusClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Position the portal menu relative to the "+" button's screen position
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setMenuPos({ top: rect.bottom + 4, left: rect.left });
    setMenuOpen((prev) => !prev);
  };

  const handleCopyClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setMenuOpen(false);
    if (onCopyScene && details && content) {
      onCopyScene({ contentSceneId: details.id, contentId: content.id });
    }
  };

  // Position the "+" button: bottom-right of the scene shape
  const plusBtnX = startScene ? 82 : 72;
  const plusBtnY = startScene ? 78 : 72;

  return (
    <svg
      width={width ?? SVGNodeScene.WIDTH}
      height={height ?? SVGNodeScene.HEIGHT}
      viewBox={viewBox ?? "0 0 100 120"}
      onClick={onClick}
      {...svgProps}
      className={"thenode " + svgProps.className ?? ""}
    >
      <g fill="none">
        <NodeInfo
          x="75"
          y="20"
          type="scene"
          infoText={`Scene ${sceneType}`}
          infoTitle={ctName}
          contentId={content.id}
          parentX={Number(x)}
          parentY={0}
          parentHeight={height ?? SVGNodeScene.HEIGHT}
          parentWidth={width ?? SVGNodeScene.WIDTH}
        />
      </g>
      <g viewBox="0 0 79 79">
        {startScene ? (
          <polygon
            points="25,0 25,120 100,60"
            stroke="rgb(118,126,133)"
            strokeWidth={strokeWidth}
            fill={valid ? "#02FCA5" : "rgb(73,80,87)"}
            id="start-scene-node"
          />
        ) : (
          <circle
            stroke="rgb(118,126,133)"
            strokeWidth={strokeWidth}
            // fill={valid ? "#02FCA5" : "#03154D"} #fc0303
            fill={
              // startScene ? "#000000" :      this was to ensure props.startScene being passed correctly
              valid ? "#02FCA5" : "rgb(73,80,87)"
            }
            cx="50%"
            cy="50%"
            x="0"
            y="0"
            r={39.5}
            filter={undefined}
            className="tour-scene-circle"
          />
        )}
        <text
          fontSize={13}
          letterSpacing={-0.336}
          fill={valid ? "#010038" : "#FFFFFF"}
          textAnchor="middle"
        >
          {tspans}
        </text>
      </g>
      {onCopyScene && (
        <foreignObject
          x={plusBtnX}
          y={plusBtnY}
          width="22"
          height="22"
          style={{ overflow: "visible" }}
        >
          <button
            onClick={handlePlusClick}
            className="flex items-center justify-center rounded-full shadow-md border border-gray-300 hover:scale-110 transition-transform"
            style={{
              width: 20,
              height: 20,
              backgroundColor: "#00C889",
              color: "white",
              fontSize: 14,
              fontWeight: "bold",
              lineHeight: 1,
              cursor: "pointer",
              padding: 0,
            }}
            title={t("pages.project.edit.copyToAnotherModule")}
          >
            +
          </button>
        </foreignObject>
      )}
      {menuOpen &&
        createPortal(
          <div
            ref={menuRef}
            className="bg-white border border-gray-200 divide-y divide-gray-100 rounded-md shadow-lg outline-none"
            style={{
              position: "fixed",
              top: menuPos.top,
              left: menuPos.left,
              zIndex: 50,
              minWidth: 200,
            }}
          >
            <div className="py-1">
              <button
                disabled
                className="group flex w-full items-center rounded-md px-3 py-1 text-sm cursor-not-allowed text-gray-400"
                onClick={(e) => e.stopPropagation()}
              >
                <svg
                  className="mr-2 w-5 h-5 stroke-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"
                  />
                </svg>
                {t("pages.project.edit.createTemplate")}
                <span className="ml-1 text-xs text-gray-300">
                  ({t("pages.project.edit.comingSoon")})
                </span>
              </button>
            </div>
            <div className="py-1">
              <button
                className="group flex w-full items-center rounded-md px-3 py-1 text-sm text-gray-900 hover:bg-gray-100"
                onClick={handleCopyClick}
              >
                <svg
                  className="mr-2 w-5 h-5 stroke-green-500"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2"
                  />
                </svg>
                {t("pages.project.edit.copyToAnotherModule")}
              </button>
            </div>
          </div>,
          document.body,
        )}
    </svg>
  );
};

SVGNodeScene.WIDTH = 150;
SVGNodeScene.HEIGHT = 150;

// we want to display text into the scene components, but there are a few issues
// - positioning is painful (think "centering")
// - we CANNOT WRAP TEXT: it will be available in SVG2 and is partially supported,
//   but as of now the only reliable and multi-platform way to auto-wrap text is
//   to do it using JS...
//
// The function below will build 1 or 2 <tspan> elements to display the title:
const buildTSpans = (title: string): JSX.Element | JSX.Element[] | null => {
  const elements = title.split(/\s+/, 2);

  switch (elements.length) {
    case 0:
      return null;
    case 1:
      if (title.length > 8) title = title.substring(0, 5) + "...";
      return (
        <tspan x="50%" y="50%" key="title" alignmentBaseline="middle" className="unselectable-text">
          {title.toLocaleUpperCase()}
        </tspan>
      );
    default:
      // eslint-disable-next-line no-case-declarations
      let [topTitle, bottomTitle] = elements;
      if (topTitle.length > 8) topTitle = topTitle.substring(0, 5) + "...";
      if (bottomTitle.length > 8) bottomTitle = bottomTitle.substring(0, 5) + "...";
      return [
        <tspan key="top-title" x="50%" y={"45%"} className="unselectable-text">
          {topTitle.toLocaleUpperCase()}
        </tspan>,
        <tspan key="bottom-title" x="50%" y={"55%"} className="unselectable-text">
          {bottomTitle.toLocaleUpperCase()}
        </tspan>,
      ];
  }
};

export default SVGNodeScene;
